import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/browser';

export function ShareCopyModal({ request, onClose }) {
  const [step, setStep] = useState('context'); // 'context' | 'generating' | 'results'
  const [contextQuestions, setContextQuestions] = useState([]);
  const [contextAnswers, setContextAnswers] = useState({});
  const [freeformContext, setFreeformContext] = useState('');
  const [useFreeform, setUseFreeform] = useState(false);
  const [activeTab, setActiveTab] = useState('reddit');
  const [generatedCopy, setGeneratedCopy] = useState({});
  const [copiedPlatform, setCopiedPlatform] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [error, setError] = useState(null);

  if (!request) return null;

  // Generate context questions when modal opens
  useEffect(() => {
    generateContextQuestions();
  }, []);

  const generateContextQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      const response = await fetch('/api/generate-context-questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: request.title,
          description: request.description,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate context questions');
      }

      const data = await response.json();
      
      if (data.questions && data.questions.length > 0) {
        setContextQuestions(data.questions);
        setUseFreeform(false);
      } else {
        // Fallback to freeform input
        setUseFreeform(true);
      }
    } catch (error) {
      console.error('Error generating context questions:', error);
      Sentry.captureException(error);
      // Fallback to freeform input
      setUseFreeform(true);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleContextAnswer = (questionId, answer) => {
    setContextAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const generateShareCopy = async () => {
    setIsGenerating(true);
    setStep('generating');
    setError(null);

    try {
      const contextData = useFreeform 
        ? { freeformContext }
        : { questions: contextQuestions, answers: contextAnswers };

      const response = await fetch('/api/generate-share-copy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request: {
            id: request.id,
            title: request.title,
            description: request.description,
            voteCount: request.voteCount || 0,
          },
          context: contextData,
          language: selectedLanguage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate share copy');
      }

      const data = await response.json();
      setGeneratedCopy(data.copy);
      setStep('results');
      
      // Track share post generation
      const { trackGenerateSharePost } = await import('/src/services/analytics/index.js');
      trackGenerateSharePost('multiple', selectedLanguage, request.id);
    } catch (error) {
      console.error('Error generating share copy:', error);
      Sentry.captureException(error);
      setError('Failed to generate share copy. Please try again.');
      setStep('context');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (platform, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPlatform(platform);
      setTimeout(() => setCopiedPlatform(null), 2000);
      
      // Track copy action
      const { trackGenerateSharePost } = await import('/src/services/analytics/index.js');
      trackGenerateSharePost(platform, selectedLanguage, request.id);
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedPlatform(platform);
      setTimeout(() => setCopiedPlatform(null), 2000);
    }
  };

  const platforms = [
    { 
      id: 'reddit', 
      name: 'Reddit', 
      color: 'bg-orange-500',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      )
    },
    { 
      id: 'tiktok', 
      name: 'TikTok/Instagram', 
      color: 'bg-pink-500',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      )
    },
    { 
      id: 'facebook', 
      name: 'Facebook', 
      color: 'bg-blue-600',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    { 
      id: 'linkedin', 
      name: 'LinkedIn', 
      color: 'bg-blue-700',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    },
  ];

  const languages = [
    { id: 'en-US', name: 'English (US)', flag: '🇺🇸' },
    { id: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
    { id: 'en-AU', name: 'English (Australia)', flag: '🇦🇺' },
    { id: 'en-CA', name: 'English (Canada)', flag: '🇨🇦' },
    { id: 'es-ES', name: 'Español (España)', flag: '🇪🇸' },
    { id: 'es-MX', name: 'Español (México)', flag: '🇲🇽' },
    { id: 'fr-FR', name: 'Français (France)', flag: '🇫🇷' },
    { id: 'de-DE', name: 'Deutsch (Deutschland)', flag: '🇩🇪' },
    { id: 'it-IT', name: 'Italiano (Italia)', flag: '🇮🇹' },
    { id: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  ];

  const canProceed = !isLoadingQuestions && (useFreeform 
    ? freeformContext.trim().length > 0
    : contextQuestions.length > 0 && Object.keys(contextAnswers).length === contextQuestions.length);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 pr-2">
              {step === 'context' && 'Get People to Vote'}
              {step === 'generating' && 'Creating Your Share Posts...'}
              {step === 'results' && 'Your Personalized Share Posts'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-1"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Request Preview */}
          <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 text-sm sm:text-base">{request.title}</h3>
            <p className="text-gray-600 text-xs sm:text-sm line-clamp-3">
              {request.description.length > 150 
                ? request.description.substring(0, 150) + '...'
                : request.description}
            </p>
            <div className="flex items-center mt-2 sm:mt-3 text-xs text-gray-500">
              <span className="flex items-center">
                {request.voteCount || 0} 👍
              </span>
              <span className="mx-2">•</span>
              <span>{request.commentCount || 0} comments</span>
            </div>
          </div>

          {/* Context Collection Step */}
          {step === 'context' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-gray-700 mb-6">
                  To create compelling posts that get people to vote for this app, help us understand your connection to this problem:
                </p>
              </div>

              {/* Language Selector */}
              {!isLoadingQuestions && (
                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language / Region:
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  >
                    {languages.map((lang) => (
                      <option key={lang.id} value={lang.id}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {isLoadingQuestions && (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-gray-600">Generating personalized questions...</p>
                </div>
              )}

              {!isLoadingQuestions && (
                <>
                  {useFreeform ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          💬 Tell us about your experience with this problem:
                        </label>
                        <textarea
                          value={freeformContext}
                          onChange={(e) => setFreeformContext(e.target.value)}
                          placeholder="Example: This would have saved me during organic chemistry when my study group fell apart right before finals..."
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm sm:text-base"
                          rows={4}
                          maxLength={300}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {freeformContext.length}/300 characters
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {contextQuestions.map((question, index) => (
                        <div key={question.id} className="space-y-3">
                          <label className="block text-sm font-medium text-gray-700">
                            {index + 1}. {question.text}
                          </label>
                          <div className="space-y-2">
                            {question.options.map((option) => (
                              <label key={option.id} className="flex items-center space-x-3 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`question-${question.id}`}
                                  value={option.id}
                                  checked={contextAnswers[question.id] === option.id}
                                  onChange={() => handleContextAnswer(question.id, option.id)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">{option.text}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-center pt-4 sm:pt-6">
                    <button
                      onClick={generateShareCopy}
                      disabled={!canProceed}
                      className={`px-6 sm:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold cursor-pointer transition-all duration-200 text-sm sm:text-base ${
                        canProceed
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:scale-105'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Create Share Posts
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Generating Step */}
          {step === 'generating' && (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Creating your personalized share posts...</p>
              <p className="text-gray-500 text-sm mt-2">This may take a few moments</p>
            </div>
          )}

          {/* Results Step */}
          {step === 'results' && (
            <div className="space-y-6">
              {/* Platform Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto pb-1">
                  {platforms.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => setActiveTab(platform.id)}
                      className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm cursor-pointer transition-colors whitespace-nowrap ${
                        activeTab === platform.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="flex items-center space-x-1 sm:space-x-2">
                        <span className="text-sm sm:text-base">{platform.icon}</span>
                        <span className="hidden sm:inline">{platform.name}</span>
                        <span className="sm:hidden">{platform.name.split('/')[0]}</span>
                      </span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Active Platform Content */}
              {generatedCopy[activeTab] && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-6">
                    <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">📝 Your personalized copy:</h4>
                    <div className="bg-white rounded-lg p-2 sm:p-4 border border-gray-200">
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed text-sm sm:text-base break-words">
                        {generatedCopy[activeTab].copy}
                      </p>
                    </div>
                  </div>

                  {generatedCopy[activeTab].suggestions && (
                    <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-6">
                      <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">📍 Where to post:</h4>
                      <ul className="space-y-2">
                        {generatedCopy[activeTab].suggestions.map((suggestion, index) => (
                          <li key={index} className="text-gray-700 flex items-start space-x-2 text-sm sm:text-base">
                            <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                            <span className="break-words">{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-center pt-2 sm:pt-0">
                    <button
                      onClick={() => copyToClipboard(activeTab, generatedCopy[activeTab].copy)}
                      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold cursor-pointer transition-all duration-200 text-sm sm:text-base ${
                        copiedPlatform === activeTab
                          ? 'bg-green-600 text-white'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:scale-105'
                      }`}
                    >
                      {copiedPlatform === activeTab ? 'Copied!' : 'Copy Text'}
                    </button>
                  </div>
                </div>
              )}

              {/* Back to Context */}
              <div className="flex justify-center pt-3 sm:pt-4">
                <button
                  onClick={() => setStep('context')}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer text-sm"
                >
                  ← Back to edit context
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500 px-2">
              Share these posts to get more people to vote for this app idea so it gets built!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 